/**
 * Costeo de recetas (Req. 10).
 *
 * CODIGO PURO: recibe las cantidades y los costos vigentes, devuelve numeros.
 * No conoce la base de datos.
 *
 * REGLA CENTRAL sobre los insumos sin costo:
 *
 *   Un insumo sin costo conocido NO rompe el calculo y NO se cuenta como cero.
 *   El total suma solo lo que si se pudo costear, el resultado se marca como
 *   INCOMPLETO, y se listan los insumos que faltan (Req. 10.7).
 *
 * Motivo: contarlo como cero abarataria la receta y mostraria una ganancia
 * inflada, que es exactamente lo que la usuaria pidio evitar. Y no calcular
 * nada dejaria la pantalla inservible mientras falte un solo precio.
 */

import { escalarItems, type ItemEscalable } from './recipes';
import { type Centavos } from './money';
import type { OrigenCosto } from './inventory';

/** Costo vigente de un insumo, tal como lo devuelve el inventario. */
export interface CostoDeInsumo {
  /** Centavos por unidad base. null = no hay ningun dato del cual partir. */
  costoPorBase: number | null;
  origen: OrigenCosto;
}

export interface RenglonCosto<T> {
  item: T;
  /** Cantidad ya escalada. */
  cantidadBase: number;
  costoPorBase: number | null;
  /** null cuando el insumo no tiene costo conocido. */
  costoCentavos: Centavos | null;
  origen: OrigenCosto;
}

export interface ResumenCosto<T> {
  renglones: Array<RenglonCosto<T>>;
  porciones: number;
  /**
   * Suma de los renglones que SI se pudieron costear.
   * Si `incompleto` es true, este total es parcial.
   */
  costoTotalCentavos: Centavos;
  costoPorPorcionCentavos: Centavos;

  /** Identificadores de los insumos sin ningun costo conocido. */
  insumosSinCosto: string[];
  /** true = algun costo viene de la ultima compra y no del promedio vigente. */
  estimado: boolean;
  /** true = falta el costo de al menos un insumo; el total NO esta completo. */
  incompleto: boolean;

  // --- Rentabilidad, cuando se conoce el precio de venta ----------------
  precioVentaCentavos: Centavos | null;
  gananciaPorPorcionCentavos: Centavos | null;
  margenPorcentaje: number | null;
}

export interface ParametrosCosteo<T extends ItemEscalable> {
  items: readonly T[];
  /** Porciones que se van a producir. */
  porciones: number;
  /** Porciones que rinde la receta tal como esta escrita. */
  rendimientoPorciones: number;
  /** Costo vigente por insumo. Lo que no aparezca se trata como desconocido. */
  costos: ReadonlyMap<string, CostoDeInsumo>;
  precioVentaCentavos?: Centavos | null;
}

const SIN_COSTO: CostoDeInsumo = { costoPorBase: null, origen: 'desconocido' };

/**
 * Costea una receta escalada a las porciones indicadas.
 *
 * Verificacion con la lasana del planteamiento (rendimiento 10):
 *   carne 1000 g a 12 c/g   = 12000 c
 *   pasta  500 g a  4 c/g   =  2000 c
 *   queso  800 g a 18 c/g   = 14400 c
 *   salsa 2000 g a  3 c/g   =  6000 c
 *   total 34400 c = $344.00 -> $34.40 por porcion
 */
export function costearReceta<T extends ItemEscalable>(
  parametros: ParametrosCosteo<T>,
): ResumenCosto<T> {
  const { items, porciones, rendimientoPorciones, costos } = parametros;
  const precioVentaCentavos = parametros.precioVentaCentavos ?? null;

  const escalados = escalarItems(items, porciones, rendimientoPorciones);

  const renglones: Array<RenglonCosto<T>> = escalados.map(({ item, cantidadBase }) => {
    const costo = costos.get(item.ingredientId) ?? SIN_COSTO;
    return {
      item,
      cantidadBase,
      costoPorBase: costo.costoPorBase,
      // El redondeo ocurre aqui, al final de la cadena, una sola vez.
      costoCentavos:
        costo.costoPorBase === null ? null : Math.round(cantidadBase * costo.costoPorBase),
      origen: costo.origen,
    };
  });

  const costoTotalCentavos = renglones.reduce((suma, r) => suma + (r.costoCentavos ?? 0), 0);

  const insumosSinCosto = renglones
    .filter((r) => r.costoCentavos === null)
    .map((r) => r.item.ingredientId);

  const incompleto = insumosSinCosto.length > 0;
  const estimado = renglones.some((r) => r.origen === 'ultima_compra');

  const costoPorPorcionCentavos =
    porciones > 0 ? Math.round(costoTotalCentavos / porciones) : 0;

  // La ganancia solo tiene sentido si el costo esta completo: restar de un
  // total parcial mostraria una ganancia mayor que la real.
  const puedeCalcularGanancia = precioVentaCentavos !== null && !incompleto;

  const gananciaPorPorcionCentavos = puedeCalcularGanancia
    ? (precioVentaCentavos as number) - costoPorPorcionCentavos
    : null;

  const margenPorcentaje =
    gananciaPorPorcionCentavos !== null &&
    precioVentaCentavos !== null &&
    precioVentaCentavos > 0
      ? (gananciaPorPorcionCentavos / precioVentaCentavos) * 100
      : null;

  return {
    renglones,
    porciones,
    costoTotalCentavos,
    costoPorPorcionCentavos,
    insumosSinCosto,
    estimado,
    incompleto,
    precioVentaCentavos,
    gananciaPorPorcionCentavos,
    margenPorcentaje,
  };
}

/**
 * Etiqueta de confianza del costo, para mostrarla junto al numero.
 * La usuaria debe poder distinguir de un vistazo que tan firme es la cifra.
 */
export type ConfianzaCosto = 'firme' | 'estimado' | 'incompleto';

export function confianzaDe<T>(resumen: ResumenCosto<T>): ConfianzaCosto {
  if (resumen.incompleto) return 'incompleto';
  if (resumen.estimado) return 'estimado';
  return 'firme';
}

export const DESCRIPCION_CONFIANZA: Readonly<Record<ConfianzaCosto, string>> = {
  firme: 'Calculado con el costo promedio de tus compras',
  estimado: 'Algun insumo se costeo con el precio de su ultima compra',
  incompleto: 'Falta el costo de algun insumo, asi que el total esta incompleto',
};
